import type {
  ColDef,
  GridApi,
  GridReadyEvent,
  RowClickedEvent,
  SelectionChangedEvent,
} from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { PlusIcon } from 'lucide-react';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SettingsListDetailLayout } from '@/components/common/SettingsListDetailLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { useGridDeselectOnClose } from '@/hooks/useGridDeselectOnClose';
import type { Employee } from '../data/databaseSchema';
import { employeeService } from '../services/employeeService';
import {
  agGridCSSClasses,
  agGridDefaultColDef,
  agGridDefaultOptions,
  agGridSettingsContainerStyles,
  agGridTheme,
  agGridThemeStyles,
} from '../theme/agGridTheme';
import { EmployeeDrawer } from './EmployeeDrawer';
import { EmployeeLocationModelDrawer } from './EmployeeLocationModelDrawer';
import { EmployeeTimeModelDrawer } from './EmployeeTimeModelDrawer';

type DetailPanelMode = 'empty' | 'selected' | 'edit' | 'add';

interface EmployeeListProps {
  employees: Employee[];
  onEdit?: (employee: Employee) => void;
  onDelete?: (employeeId: string) => void;
  onAdd?: () => void;
  onView?: (employee: Employee) => void;
  isLoading?: boolean;
  onEmployeeSaved?: () => void;
}

export const EmployeeList: React.FC<EmployeeListProps> = ({
  employees,
  onEdit,
  onDelete,
  onAdd,
  onView: _onView,
  isLoading = false,
  onEmployeeSaved,
}) => {
  const gridRef = useRef<AgGridReact>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const justSavedRef = useRef(false);
  const suppressRowDetailRef = useRef(false);
  const [panelMode, setPanelMode] = useState<DetailPanelMode>('empty');
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  const [isTimeModelDrawerOpen, setIsTimeModelDrawerOpen] = useState(false);
  const [selectedEmployeeForTimeModel, setSelectedEmployeeForTimeModel] = useState<Employee | null>(
    null
  );
  const [isLocationModelDrawerOpen, setIsLocationModelDrawerOpen] = useState(false);
  const [selectedEmployeeForLocationModel, setSelectedEmployeeForLocationModel] =
    useState<Employee | null>(null);
  const [isWideScreen, setIsWideScreen] = useState(() => window.innerWidth > 1900);

  const detailOpen = panelMode !== 'empty' || isTimeModelDrawerOpen || isLocationModelDrawerOpen;

  useGridDeselectOnClose(gridRef, detailOpen);

  const closeAllDetailPanels = useCallback(() => {
    setPanelMode('empty');
    setSelectedEmployee(null);
    setEditingEmployee(null);
    setIsTimeModelDrawerOpen(false);
    setIsLocationModelDrawerOpen(false);
    setSelectedEmployeeForTimeModel(null);
    setSelectedEmployeeForLocationModel(null);
  }, []);

  const handleFormClose = useCallback(() => {
    if (justSavedRef.current) {
      justSavedRef.current = false;
      setEditingEmployee(null);
      return;
    }
    setPanelMode((mode) => (mode === 'edit' ? 'selected' : 'empty'));
    setEditingEmployee(null);
  }, []);

  const clearRowSelection = useCallback(() => {
    gridApi?.deselectAll();
  }, [gridApi]);

  const handleViewClose = useCallback(() => {
    closeAllDetailPanels();
    clearRowSelection();
  }, [clearRowSelection, closeAllDetailPanels]);

  const handleSelectionChanged = useCallback(
    (event: SelectionChangedEvent<Employee>) => {
      if (suppressRowDetailRef.current) {
        return;
      }
      const selected = event.api.getSelectedRows();
      if (selected.length === 0) {
        setPanelMode((mode) => (mode === 'selected' ? 'empty' : mode));
        setSelectedEmployee(null);
        return;
      }
      closeAllDetailPanels();
      setSelectedEmployee(selected[0]);
      setPanelMode('selected');
    },
    [closeAllDetailPanels]
  );

  const handleRowClicked = useCallback(
    (event: RowClickedEvent<Employee>) => {
      const target = event.event?.target;
      const isCellButtonClick = target instanceof Element && target.closest('button') !== null;
      if (suppressRowDetailRef.current || isCellButtonClick) {
        suppressRowDetailRef.current = false;
        return;
      }
      if (!event.data) return;
      closeAllDetailPanels();
      setSelectedEmployee(event.data);
      setPanelMode('selected');
    },
    [closeAllDetailPanels]
  );

  const handleDeleteEmployee = useCallback(
    async (employee: Employee) => {
      if (
        !onDelete ||
        !window.confirm(
          `Are you sure you want to delete employee "${employee.fullname}" (${employee.employee_id})?`
        )
      ) {
        return;
      }
      setIsDeleting(employee.id);
      try {
        await employeeService.deleteEmployee(employee.id);
        onDelete(employee.employee_id);
        if (onEmployeeSaved) {
          onEmployeeSaved();
        }
        clearRowSelection();
        closeAllDetailPanels();
      } catch (error) {
        alert(
          `Failed to delete employee: ${error instanceof Error ? error.message : 'Unknown error'}`
        );
      } finally {
        setIsDeleting(null);
      }
    },
    [clearRowSelection, closeAllDetailPanels, onDelete, onEmployeeSaved]
  );

  const openTimeModelDrawer = useCallback(
    (employee: Employee) => {
      suppressRowDetailRef.current = true;
      closeAllDetailPanels();
      clearRowSelection();
      setSelectedEmployeeForTimeModel(employee);
      setIsTimeModelDrawerOpen(true);
    },
    [clearRowSelection, closeAllDetailPanels]
  );

  const openLocationModelDrawer = useCallback(
    (employee: Employee) => {
      suppressRowDetailRef.current = true;
      closeAllDetailPanels();
      clearRowSelection();
      setSelectedEmployeeForLocationModel(employee);
      setIsLocationModelDrawerOpen(true);
    },
    [clearRowSelection, closeAllDetailPanels]
  );

  const DetailsCellRenderer = (params: any) => {
    const { data } = params;
    const timeModel = data?.time_model || 'Not set';
    const isNotSet = !data?.time_model || data?.time_model === 'Not set';
    return (
      <Button
        type="button"
        size="sm"
        className={isNotSet ? 'bg-red-400 text-white hover:bg-warning-600 mx-2' : 'mx-0.5'}
        onClick={(e) => {
          e.stopPropagation();
          openTimeModelDrawer(data);
        }}
        title={`View employee time model: ${timeModel}`}
      >
        🕙{timeModel}
      </Button>
    );
  };

  const LocationCellRenderer = (params: any) => {
    const { data } = params;
    const location = data?.location || 'Not set';
    const isNotSet = !data?.location || data?.location === 'Not set';
    return (
      <Button
        type="button"
        size="sm"
        className={isNotSet ? 'bg-red-400 text-white hover:bg-warning-600 mx-2' : 'mx-0.5'}
        onClick={(e) => {
          e.stopPropagation();
          openLocationModelDrawer(data);
        }}
        title={`View employee location model: ${location}`}
      >
        🏢{location}
      </Button>
    );
  };

  // Column definitions for the employee grid
  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: 'Full Name',
        field: 'fullname',
        width: 160,
        minWidth: 140,
        maxWidth: 180,
        pinned: 'left',
        lockPosition: 'left',
        sortable: true,
        filter: 'agSetColumnFilter',
        filterParams: {
          buttons: ['reset', 'apply'],
        },
        comparator: (a: string, b: string) => {
          const sa = a == null ? '' : String(a);
          const sb = b == null ? '' : String(b);
          return sa.localeCompare(sb, undefined, { numeric: true, sensitivity: 'base' });
        },
        headerClass: agGridCSSClasses.modernHeader,
        cellClass: agGridCSSClasses.modernCell,
        cellRenderer: (params: any) => {
          return <div className="font-semibold text-text-primary">{params.value}</div>;
        },
      },
      {
        headerName: 'Emp ID',
        field: 'employee_id',
        pinned: 'left',
        width: 110,
        minWidth: 100,
        maxWidth: 120,
        sortable: true,
        filter: 'agSetColumnFilter',
        filterParams: {
          buttons: ['reset', 'apply'],
        },
        comparator: (a: string, b: string) => {
          const sa = a == null ? '' : String(a);
          const sb = b == null ? '' : String(b);
          return sa.localeCompare(sb, undefined, { numeric: true, sensitivity: 'base' });
        },
        headerClass: agGridCSSClasses.modernHeader,
        cellClass: agGridCSSClasses.modernCell,
        cellRenderer: (params: any) => {
          return (
            <span className="rounded-sm p-2 text-xs font-semibold bg-muted text-text-secondary">
              {params.value}
            </span>
          );
        },
      },
      {
        headerName: 'Email',
        field: 'email',
        flex: 1,
        minWidth: 100,
        headerClass: agGridCSSClasses.modernHeader,
        cellClass: agGridCSSClasses.modernCell,
      },
      {
        headerName: 'Role',
        field: 'role',
        width: 90,
        minWidth: 80,
        maxWidth: 100,
        headerClass: agGridCSSClasses.modernHeader,
        cellClass: agGridCSSClasses.modernCell,
        cellRenderer: (params: any) => {
          const role = params.value || 'user';
          const roleClass =
            role === 'admin'
              ? 'bg-primary-500/20 text-primary-600'
              : 'bg-muted text-text-secondary';

          return (
            <span className={`rounded px-2 py-1 text-xs font-semibold capitalize ${roleClass}`}>
              {role}
            </span>
          );
        },
      },
      {
        headerName: 'Status',
        field: 'status',
        width: 100,
        minWidth: 90,
        maxWidth: 110,
        headerClass: agGridCSSClasses.modernHeader,
        cellClass: agGridCSSClasses.modernCell,
        cellRenderer: (params: any) => {
          const status = params.value ?? 'active';
          const statusClass =
            status === 'active'
              ? 'bg-success-500/20 text-success-600'
              : status === 'suspended'
                ? 'bg-danger-500/20 text-danger-600'
                : 'bg-muted text-text-secondary';

          return (
            <span className={`rounded px-2 py-1 text-xs font-semibold ${statusClass}`}>
              {String(status).toUpperCase()}
            </span>
          );
        },
      },
      {
        headerName: 'Time Model',
        field: 'time_model',
        flex: 1,
        minWidth: 105,
        pinned: 'right',
        lockPosition: 'right',
        sortable: true,
        valueGetter: (p) => {
          const v = p.data?.time_model;
          return v == null || v === '' ? 'Not set' : String(v);
        },
        comparator: (a: string, b: string) =>
          String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' }),
        cellRenderer: DetailsCellRenderer,
        cellClass: agGridCSSClasses.leftAlignCell,
        headerClass: agGridCSSClasses.modernHeader,
        filter: 'agSetColumnFilter',
        filterParams: {
          buttons: ['reset', 'apply'],
        },
        editable: false,
      },
      {
        headerName: 'Location',
        field: 'location',
        flex: 1,
        minWidth: 140,
        pinned: 'right',
        lockPosition: 'right',
        sortable: true,
        valueGetter: (p) => {
          const v = p.data?.location;
          return v == null || v === '' ? 'Not set' : String(v);
        },
        comparator: (a: string, b: string) =>
          String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' }),
        cellRenderer: LocationCellRenderer,
        cellClass: agGridCSSClasses.leftAlignCell,
        headerClass: agGridCSSClasses.modernHeader,
        filter: 'agSetColumnFilter',
        filterParams: {
          buttons: ['reset', 'apply'],
        },
        editable: false,
      },
    ],
    [openLocationModelDrawer, openTimeModelDrawer]
  );

  // Search functionality
  const handleSearch = useCallback(
    (searchValue: string) => {
      setSearchTerm(searchValue);
      if (gridApi) {
        gridApi.setGridOption('quickFilterText', searchValue);
      }
    },
    [gridApi]
  );

  // Clear search
  const handleClearSearch = useCallback(() => {
    setSearchTerm('');
    if (gridApi) {
      gridApi.setGridOption('quickFilterText', '');
    }
  }, [gridApi]);

  // Export functionality
  const handleExportCSV = useCallback(() => {
    if (gridApi) {
      gridApi.exportDataAsCsv({
        fileName: 'employees.csv',
        columnKeys: ['id', 'employee_id', 'fullname', 'email', 'status', 'created_at'],
      });
    }
  }, [gridApi]);

  // Grid event handlers
  const handleGridReady = useCallback((event: GridReadyEvent) => {
    setGridApi(event.api);
  }, []);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1901px)');
    const onChange = () => setIsWideScreen(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    if (!gridApi) return;
    gridApi.setColumnsVisible(['role', 'status'], !detailOpen);
    gridApi.setColumnsVisible(['email'], !detailOpen || isWideScreen);
  }, [gridApi, detailOpen, isWideScreen]);

  const handleFirstDataRendered = useCallback(() => {
    // Data rendered
  }, []);

  const handleRowDataUpdated = useCallback(() => {
    // Row data updated
  }, []);

  // Grid options
  const gridOptions = {
    ...agGridDefaultOptions,
    theme: agGridTheme,
    suppressContextMenu: true,
    cellSelection: false,
    onGridReady: handleGridReady,
    onFirstDataRendered: handleFirstDataRendered,
    onRowDataUpdated: handleRowDataUpdated,
    onSelectionChanged: handleSelectionChanged,
    onRowClicked: handleRowClicked,
    rowSelection: { mode: 'singleRow' as const, checkboxes: false },
    quickFilterText: searchTerm,
    rowNumbers: { minWidth: 25 },
    alternateBackground: true,
  };

  const detailVariant =
    isTimeModelDrawerOpen || isLocationModelDrawerOpen ? ('wide' as const) : ('default' as const);

  return (
    <SettingsListDetailLayout
      detailOpen={detailOpen}
      detailVariant={detailVariant}
      detail={
        isTimeModelDrawerOpen ? (
          <EmployeeTimeModelDrawer
            isOpen={isTimeModelDrawerOpen}
            onClose={closeAllDetailPanels}
            employee={selectedEmployeeForTimeModel}
          />
        ) : isLocationModelDrawerOpen ? (
          <EmployeeLocationModelDrawer
            isOpen={isLocationModelDrawerOpen}
            onClose={closeAllDetailPanels}
            employee={selectedEmployeeForLocationModel}
          />
        ) : panelMode === 'edit' || panelMode === 'add' ? (
          <EmployeeDrawer
            isOpen={true}
            onClose={handleFormClose}
            onSave={(employee) => {
              if (onEdit) {
                onEdit(employee);
              }
            }}
            onEntrySaved={() => {
              justSavedRef.current = true;
              clearRowSelection();
              closeAllDetailPanels();
              if (onEmployeeSaved) {
                onEmployeeSaved();
              }
            }}
            employee={editingEmployee}
            isEdit={panelMode === 'edit'}
          />
        ) : panelMode === 'selected' && selectedEmployee ? (
          <EmployeeDrawer
            isOpen={true}
            readOnly
            employee={selectedEmployee}
            onClose={handleViewClose}
            onSave={() => {}}
            onEditRequest={
              onEdit
                ? () => {
                    const employee = selectedEmployee;
                    closeAllDetailPanels();
                    setSelectedEmployee(employee);
                    setEditingEmployee(employee);
                    setPanelMode('edit');
                    onEdit(employee);
                  }
                : undefined
            }
            onDeleteRequest={
              onDelete
                ? () => {
                    void handleDeleteEmployee(selectedEmployee);
                  }
                : undefined
            }
            isDeleteDisabled={isDeleting === selectedEmployee.id}
          />
        ) : null
      }
      list={
        <div className="flex min-h-0 flex-1 flex-col py-2">
          <div className="mb-5 flex shrink-0 items-center justify-between">
            <div className="flex items-center gap-3 bg-white">
              <div className="relative">
                <Input
                  type="text"
                  placeholder="Search employees..."
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="w-[250px] pr-8"
                />
                {searchTerm && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-xs"
                    className="absolute top-1/2 right-1 -translate-y-1/2"
                    onClick={handleClearSearch}
                    title="Clear search"
                  >
                    ×
                  </Button>
                )}
              </div>

              <Button type="button" variant="secondary" onClick={handleExportCSV}>
                Export CSV
              </Button>
            </div>

            {onAdd && (
              <Button
                type="button"
                onClick={() => {
                  closeAllDetailPanels();
                  clearRowSelection();
                  setPanelMode('add');
                  onAdd();
                }}
              >
                <PlusIcon data-icon="inline-start" />
                Add Employee
              </Button>
            )}
          </div>

          {!isLoading && employees.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-4 p-12 text-center">
              <div className="text-5xl text-text-muted">👥</div>
              <div className="text-lg font-medium text-text-secondary">No employees found</div>
              <div className="text-sm text-text-muted">Add your first employee to get started</div>
              {onAdd && (
                <Button
                  type="button"
                  className="mt-2"
                  onClick={() => {
                    closeAllDetailPanels();
                    clearRowSelection();
                    setPanelMode('add');
                    onAdd();
                  }}
                >
                  <PlusIcon data-icon="inline-start" />
                  Add Employee
                </Button>
              )}
            </div>
          ) : (
            <>
              <style>{`${agGridThemeStyles}
.employee-list-grid.ag-theme-material .ag-header-cell .ag-icon {
  display: inherit !important;
}
.employee-list-grid.ag-theme-material .ag-header-cell .ag-header-cell-menu-button {
  display: inherit !important;
}
.employee-list-grid.ag-theme-material .ag-header-cell-sortable {
  cursor: pointer !important;
}
.employee-list-grid.ag-theme-material .ag-header-cell-sortable:hover {
  cursor: pointer !important;
}
`}</style>
              <div
                className="ag-theme-material employee-list-grid min-h-0 flex-1"
                style={agGridSettingsContainerStyles}
              >
                <AgGridReact
                  ref={gridRef}
                  rowData={employees}
                  getRowId={(params) => params.data.id}
                  columnDefs={columnDefs}
                  defaultColDef={agGridDefaultColDef}
                  gridOptions={gridOptions}
                  animateRows={true}
                  enableCellTextSelection={true}
                  ensureDomOrder={true}
                />

                {isLoading && (
                  <div className=" items-center gap-2 px-8 py-4 text-sm font-medium text-text-secondary">
                    <Spinner className="size-4" />
                    Loading employees...
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      }
    />
  );
};
